'use client';

import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { NavbarIsland } from './NavbarIsland';
import { AnalyticsConsentManager } from './AnalyticsConsentManager';
import { NativeAppLifecycle } from './NativeAppLifecycle';
import { ScrollToTop } from './ScrollToTop';
import { OfflineAccountLifecycle } from './OfflineAccountLifecycle';
import { SharedOutingMapProvider } from '@/app/_components/companion/SharedOutingMapProvider';
import { RecordingHost } from '@/app/_components/companion/RecordingHost';
import { isCompanionAppView } from '@/lib/companion/chrome';
import { companionExampleModeSnapshot, subscribeCompanionExampleMode } from '@/lib/companion/chrome-example-mode';

/**
 * Signed-in Companion routes run as an app: the global navbar and footer give
 * way to the Companion frame. The root layout's head script marks the document
 * before paint; after hydration the chrome stops rendering and the marker
 * follows client navigation. Logged out, nothing changes.
 */
function useCompanionAppView(pathname: string): boolean {
  // A logged-out example page (an expired session's cookie can linger) keeps the site chrome.
  const appView = useSyncExternalStore(
    subscribeCompanionExampleMode,
    () => isCompanionAppView(pathname, document.cookie) && !companionExampleModeSnapshot(),
    () => false,
  );
  useEffect(() => {
    const root = document.documentElement;
    if (appView) root.setAttribute('data-companion-app', '1');
    else root.removeAttribute('data-companion-app');
  }, [appView]);
  return appView;
}

/** Keep the root server layout static and the public iframe free of account UI. */
export function SiteShell({ children, footer }: { children: ReactNode; footer: ReactNode }) {
  const pathname = usePathname();
  const appView = useCompanionAppView(pathname);
  if (pathname === '/embed/conditions') return <RecordingHost><main id="main">{children}</main></RecordingHost>;
  const atlas = pathname === '/conditions/global';
  return <SharedOutingMapProvider><RecordingHost>
    <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[1100] focus:px-4 focus:py-2 focus:bg-navy focus:text-white focus:rounded-sm focus:text-sm focus:font-semibold">Skip to content</a>
    <AnalyticsConsentManager />
    <NativeAppLifecycle />
    <OfflineAccountLifecycle />
    <ScrollToTop />
    {!appView && <div data-site-chrome="navbar"><NavbarIsland /></div>}
    <main id="main" className={atlas ? 'h-svh overflow-hidden pt-16' : appView ? 'min-h-screen' : 'min-h-screen pt-16'}>{children}</main>
    {!atlas && !appView && <div data-site-chrome="footer">{footer}</div>}
  </RecordingHost></SharedOutingMapProvider>;
}
